import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '../ui/Drawer';
import { useToast } from '../ui/Toast';
import {
  SENIORITY_OPTIONS, INDUSTRY_SUGGESTIONS, cardToItem, listBlueprintCards, publishBlueprint, titleCase,
  updateBlueprintDraft,
} from '../../api/blueprints';

const EMPTY_ITEM = { topic: '', tier: 3, weight: 10, mustAsk: false, canonicalQuestion: '' };
const lines = (v) => (v || []).join('\n');
const toList = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

/**
 * Blueprint detail / draft editor. `mode`:
 *   'preview' - read-only view of a published version (content from the version)
 *   'edit'    - edit an org (or, for SUPER_ADMIN, global) draft, then publish a new version
 */
export default function BlueprintEditor({ open, onClose, mode, detail, version, orgId, onSaved }) {
  const toast = useToast();
  const readOnly = mode !== 'edit';
  const source = readOnly ? version : detail;
  const [meta, setMeta] = useState({});
  const [items, setItems] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(30);
  const [expanded, setExpanded] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    if (readOnly) {
      setMeta({ name: version.name, roleFamily: version.roleFamily, industry: version.industry, seniority: version.seniority });
      setItems(version.content?.items || []);
      setTotalMinutes(version.content?.totalMinutes || 30);
    } else {
      const s = detail.summary;
      setMeta({ name: s.name, roleFamily: s.roleFamily, industry: s.industry || '', seniority: s.seniority || '', tags: (s.tags || []).join(', ') });
      setItems(detail.draft?.items || []);
      setTotalMinutes(detail.draft?.totalMinutes || 30);
      listBlueprintCards(orgId).then((r) => setCards(r.data || [])).catch(() => setCards([]));
    }
    setExpanded(null);
  }, [open, source, readOnly, detail, version, orgId]);

  const weightSum = useMemo(() => items.reduce((a, i) => a + (Number(i.weight) || 0), 0), [items]);
  const setItem = (idx, patch) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const move = (idx, d) => setItems((xs) => {
    const j = idx + d;
    if (j < 0 || j >= xs.length) return xs;
    const copy = xs.slice();
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  });

  const addFromCard = async () => {
    if (!cardId) return;
    try {
      const res = await cardToItem(cardId, orgId);
      setItems((xs) => [...xs, { ...res.data, weight: 10 }]);
      setCardId('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add topic');
    }
  };

  const payload = () => ({
    name: meta.name,
    roleFamily: meta.roleFamily,
    industry: meta.industry || '',
    seniority: meta.seniority || '',
    tags: (meta.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
    draft: { totalMinutes: Number(totalMinutes) || 30, items },
  });

  const save = async (andPublish) => {
    setBusy(true);
    try {
      await updateBlueprintDraft(detail.summary.id, payload());
      if (andPublish) {
        const res = await publishBlueprint(detail.summary.id);
        toast.success(`Published v${res.data.versionNo}. Jobs keep their pinned version until you change it.`);
      } else {
        toast.success('Draft saved');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm disabled:bg-slate-50';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={readOnly ? `${meta.name || 'Blueprint'}${version ? ` · v${version.versionNo}` : ''}` : `Edit draft: ${meta.name || ''}`}
      footer={readOnly ? null : (
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={() => save(false)}>Save draft</button>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !items.length} onClick={() => save(true)}>Save and publish</button>
        </div>
      )}
    >
      {!source ? null : (
        <div className="space-y-5 text-sm">
          {readOnly && <p className="text-xs text-slate-500">Published versions are read-only. Copy the blueprint to make an editable version for your organization.</p>}
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 text-xs text-slate-500">Name
              <input className={input} disabled={readOnly} value={meta.name || ''} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
            </label>
            <label className="text-xs text-slate-500">Role
              <input className={input} disabled={readOnly} value={meta.roleFamily || ''} onChange={(e) => setMeta({ ...meta, roleFamily: e.target.value })} />
            </label>
            <label className="text-xs text-slate-500">Industry
              <input className={input} disabled={readOnly} list="bp-industries" value={meta.industry || ''} onChange={(e) => setMeta({ ...meta, industry: e.target.value })} />
              <datalist id="bp-industries">{INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}</datalist>
            </label>
            <label className="text-xs text-slate-500">Seniority
              <select className={input} disabled={readOnly} value={meta.seniority || ''} onChange={(e) => setMeta({ ...meta, seniority: e.target.value })}>
                <option value="">Not set</option>
                {SENIORITY_OPTIONS.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Planned minutes
              <input type="number" min={10} max={120} className={input} disabled={readOnly} value={totalMinutes} onChange={(e) => setTotalMinutes(e.target.value)} />
            </label>
            {!readOnly && (
              <label className="col-span-2 text-xs text-slate-500">Tags (used to match the org default to roles)
                <input className={input} value={meta.tags || ''} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} />
              </label>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-ink">Topics</h3>
              <span className={`text-xs ${weightSum === 100 ? 'text-slate-500' : 'text-amber-600'}`}>
                Weights total {weightSum}% {weightSum !== 100 && '(normalised to 100% when planned)'}
              </span>
            </div>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              <div className="grid grid-cols-[1fr_64px_64px_64px_70px] gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400">
                <span>Topic</span><span>Tier</span><span>Weight</span><span>Minutes</span><span>Must ask</span>
              </div>
              {items.map((it, idx) => (
                <div key={it.key || idx} className="px-3 py-2">
                  <div className="grid grid-cols-[1fr_64px_64px_64px_70px] gap-2 items-center">
                    <button type="button" className="text-left truncate text-slate-800 hover:text-teal-700" onClick={() => setExpanded(expanded === idx ? null : idx)}>
                      {expanded === idx ? '▾ ' : '▸ '}{it.topic || 'Untitled topic'}
                    </button>
                    <input type="number" min={1} max={5} className={input} disabled={readOnly} value={it.tier ?? ''} onChange={(e) => setItem(idx, { tier: Number(e.target.value) })} />
                    <input type="number" min={1} max={100} className={input} disabled={readOnly} value={it.weight ?? ''} onChange={(e) => setItem(idx, { weight: Number(e.target.value) })} />
                    <input type="number" min={1} max={30} className={input} disabled={readOnly} value={it.estimatedMinutes ?? ''} placeholder="auto" onChange={(e) => setItem(idx, { estimatedMinutes: e.target.value ? Number(e.target.value) : null })} />
                    <input type="checkbox" disabled={readOnly} checked={!!it.mustAsk} onChange={(e) => setItem(idx, { mustAsk: e.target.checked, mustAskText: e.target.checked ? (it.mustAskText || it.canonicalQuestion || '') : null })} />
                  </div>
                  {expanded === idx && (
                    <div className="mt-3 grid gap-2">
                      <label className="text-xs text-slate-500">Topic name
                        <input className={input} disabled={readOnly} value={it.topic || ''} onChange={(e) => setItem(idx, { topic: e.target.value })} />
                      </label>
                      <label className="text-xs text-slate-500">{it.mustAsk ? 'Must-ask text (asked exactly as written)' : 'Canonical question'}
                        <textarea rows={2} className={input} disabled={readOnly}
                          value={(it.mustAsk ? it.mustAskText : it.canonicalQuestion) || ''}
                          onChange={(e) => setItem(idx, it.mustAsk ? { mustAskText: e.target.value, canonicalQuestion: e.target.value } : { canonicalQuestion: e.target.value })} />
                      </label>
                      <label className="text-xs text-slate-500">Objective
                        <input className={input} disabled={readOnly} value={it.objective || ''} onChange={(e) => setItem(idx, { objective: e.target.value })} />
                      </label>
                      <label className="text-xs text-slate-500">Expected evidence (one per line, never spoken)
                        <textarea rows={3} className={input} disabled={readOnly} value={lines(it.expectedEvidence)} onChange={(e) => setItem(idx, { expectedEvidence: toList(e.target.value) })} />
                      </label>
                      <label className="text-xs text-slate-500">Allowed follow-ups (one per line)
                        <textarea rows={2} className={input} disabled={readOnly} value={lines(it.allowedFollowUps)} onChange={(e) => setItem(idx, { allowedFollowUps: toList(e.target.value) })} />
                      </label>
                      <label className="text-xs text-slate-500">Approved scenario contexts (one per line)
                        <textarea rows={2} className={input} disabled={readOnly} value={lines(it.scenarioSkins)} onChange={(e) => setItem(idx, { scenarioSkins: toList(e.target.value) })} />
                      </label>
                      {!readOnly && (
                        <div className="flex gap-2">
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => move(idx, -1)}>Move up</button>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => move(idx, 1)}>Move down</button>
                          <button type="button" className="btn btn-sm text-red-600 border border-red-200" onClick={() => setItems((xs) => xs.filter((_, i) => i !== idx))}>Remove</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {!items.length && <div className="px-3 py-6 text-center text-slate-400 text-xs">No topics yet.</div>}
            </div>
            {!readOnly && (
              <div className="flex flex-wrap gap-2 mt-3">
                <select className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[200px]" value={cardId} onChange={(e) => setCardId(e.target.value)}>
                  <option value="">Add a topic from a card…</option>
                  {cards.map((c) => (
                    <option key={c.id} value={c.id}>{c.global ? '' : '[Org] '}{c.domain} · {c.topic} (tier {c.tier})</option>
                  ))}
                </select>
                <button type="button" className="btn btn-sm btn-secondary" disabled={!cardId} onClick={addFromCard}>Add</button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setItems((xs) => [...xs, { ...EMPTY_ITEM }]); setExpanded(items.length); }}>Blank topic</button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setItems((xs) => [...xs, { ...EMPTY_ITEM, topic: 'Must-ask question', mustAsk: true, mustAskText: '' }]); setExpanded(items.length); }}>Must-ask question</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
